import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TaskStatusOutput } from "@orsane/schema/tasks";
import { flexRender, type Row } from "@tanstack/react-table";
import { GripVertical } from "lucide-react";
import { memo, useMemo } from "react";
import { TableCell, TableRow } from "@/components/ui/table";

interface SortableTaskStatusRowProps {
    status: TaskStatusOutput;
    children: React.ReactNode;
    isFirst: boolean;
    isLast: boolean;
}

export const NonSortableTaskStatusRow = memo(({ row }: { row: Row<TaskStatusOutput> }) => {
    if (!row) return null;
    return (
        <TableRow>
            <TableCell className="w-10"></TableCell>
            {row.getVisibleCells().map((cell) => (
                <TableCell
                    className={[
                        cell.column.columnDef.meta?.width ? `w-[${cell.column.columnDef.meta?.width}px]` : "",
                        cell.column.columnDef.meta?.align ? `text-${cell.column.columnDef.meta?.align}` : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                    key={cell.id}
                >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
            ))}
        </TableRow>
    );
});

export const SortableTaskStatusRow = memo(({ status, children, isFirst, isLast }: SortableTaskStatusRowProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: status.id.toString(),
        disabled: isFirst || isLast,
    });

    const style = useMemo(
        () => ({
            position: isDragging ? ("relative" as const) : undefined,
            zIndex: isDragging ? 100 : undefined,
            transform: CSS.Transform.toString(transform),
            transition,
        }),
        [isDragging, transform, transition]
    );

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell className="w-10">
                {!isFirst && !isLast && (
                    <div
                        className="grid place-items-center size-8 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical className="size-4" />
                        <span className="sr-only">Drag to reorder</span>
                    </div>
                )}
            </TableCell>
            {children}
        </TableRow>
    );
});

SortableTaskStatusRow.displayName = "SortableTaskStatusRow";
